// src/merch/illustrate.js
//
// "Illustrated" look for face photos, so they sit better next to the
// painted jersey art: edge-preserving smoothing (bilateral), punchier
// contrast + colour, and a faint ink edge. strength 0..1.
// Runs on-device; keeps transparency (background-removed photos).

const MAX = 600; // working size — plenty for a ~7 mm printed face

export function illustrate(src, strength = 0.6) {
  const s = Math.max(0, Math.min(1, strength));
  const k = Math.min(1, MAX / Math.max(src.width, src.height));
  const w = Math.max(1, Math.round(src.width * k)), h = Math.max(1, Math.round(src.height * k));
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(src, 0, 0, w, h);
  if (s <= 0) return cv;

  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const n = w * h;
  let R = new Float32Array(n), G = new Float32Array(n), B = new Float32Array(n);
  const A = new Float32Array(n);
  for (let i = 0; i < n; i++) { R[i] = d[i * 4]; G[i] = d[i * 4 + 1]; B[i] = d[i * 4 + 2]; A[i] = d[i * 4 + 3] / 255; }

  // ---- bilateral smoothing (alpha-weighted so no dark halos at cut edges)
  const r = 3;
  const sigS = 2.2, sigR = 14 + 34 * s;
  const spatial = [];
  for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
    const ws = Math.exp(-(dx * dx + dy * dy) / (2 * sigS * sigS));
    if (ws > 0.02) spatial.push(dx, dy, ws);
  }
  const lut = new Float32Array(766);
  for (let i = 0; i < 766; i++) { const t = i / (sigR * 1.7); lut[i] = Math.exp(-0.5 * t * t); }
  const passes = s > 0.45 ? 2 : 1;
  for (let p = 0; p < passes; p++) {
    const R2 = new Float32Array(n), G2 = new Float32Array(n), B2 = new Float32Array(n);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (A[i] === 0) continue;
        const r0 = R[i], g0 = G[i], b0 = B[i];
        let sr = 0, sg = 0, sb = 0, sw = 0;
        for (let q = 0; q < spatial.length; q += 3) {
          const xx = x + spatial[q], yy = y + spatial[q + 1];
          if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          const j = yy * w + xx;
          const a = A[j];
          if (a === 0) continue;
          const diff = Math.abs(R[j] - r0) + Math.abs(G[j] - g0) + Math.abs(B[j] - b0);
          const wt = spatial[q + 2] * lut[diff > 765 ? 765 : diff | 0] * a;
          sr += R[j] * wt; sg += G[j] * wt; sb += B[j] * wt; sw += wt;
        }
        R2[i] = sr / sw; G2[i] = sg / sw; B2[i] = sb / sw;
      }
    }
    R = R2; G = G2; B = B2;
  }

  // ---- faint ink edge (Sobel on luma of the smoothed image)
  const L = new Float32Array(n);
  for (let i = 0; i < n; i++) L[i] = 0.299 * R[i] + 0.587 * G[i] + 0.114 * B[i];
  const ink = 0.35 * s;

  // ---- tone: contrast + gentle S-curve on BRIGHTNESS only (per-channel
  // curves push skin orange), and ease saturation down as strength rises
  // so it sits with the painted, muted jersey art
  const con = 1 + 0.3 * s, sat = 1 - 0.28 * s, sm = 0.3 * s;
  const tone = (v) => {
    let c = v / 255;
    c = 0.5 + (c - 0.5) * con;
    c = c < 0 ? 0 : c > 1 ? 1 : c;
    c = c + (c * c * (3 - 2 * c) - c) * sm;
    return c * 255;
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (A[i] === 0) continue;
      let r0 = R[i], g0 = G[i], b0 = B[i];
      // saturation, then contrast on luma (shift all channels together)
      const l0 = 0.299 * r0 + 0.587 * g0 + 0.114 * b0;
      r0 = l0 + (r0 - l0) * sat; g0 = l0 + (g0 - l0) * sat; b0 = l0 + (b0 - l0) * sat;
      const dl = tone(l0) - l0;
      r0 += dl; g0 += dl; b0 += dl;
      // edges
      if (x > 0 && y > 0 && x < w - 1 && y < h - 1) {
        const gx = L[i - w + 1] + 2 * L[i + 1] + L[i + w + 1] - L[i - w - 1] - 2 * L[i - 1] - L[i + w - 1];
        const gy = L[i + w - 1] + 2 * L[i + w] + L[i + w + 1] - L[i - w - 1] - 2 * L[i - w] - L[i - w + 1];
        let e = Math.sqrt(gx * gx + gy * gy) / 400; // ~0..1
        e = e < 0.15 ? 0 : Math.min(1, (e - 0.15) / 0.6);
        const f = 1 - e * ink;
        r0 *= f; g0 *= f; b0 *= f;
      }
      d[i * 4] = r0 < 0 ? 0 : r0 > 255 ? 255 : r0;
      d[i * 4 + 1] = g0 < 0 ? 0 : g0 > 255 ? 255 : g0;
      d[i * 4 + 2] = b0 < 0 ? 0 : b0 > 255 ? 255 : b0;
    }
  }
  ctx.putImageData(img, 0, 0);
  return cv;
}
